import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Briefcase, GraduationCap, 
  Heart, Users, Star, Calendar, Edit, Crown, CheckCircle, XCircle, Shield, Clock, ArrowRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { userAPI, premiumAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import BackButton from '../components/ui/BackButton';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { COUNTRIES } from '../constants/countries';

// Helper function to format location display
const getLocationDisplay = (profile) => {
  if (!profile) return null;
  
  // Priority 1: Use country and province fields (new format)
  if (profile.country && profile.province) {
    const countryName = COUNTRIES.find(c => c.code === profile.country)?.name || profile.country;
    return `${profile.province}, ${countryName}`;
  }
  
  // Priority 2: Extract province from location field (legacy format)
  // Location format is "City, Province" - we want to show "Province, Country"
  if (profile.location) {
    const parts = profile.location.split(',');
    if (parts.length >= 2) {
      // Second part is province, hide city for privacy
      const province = parts[1].trim();
      
      // Try to get country from address object
      if (profile.address && profile.address.country) {
        const countryName = COUNTRIES.find(c => c.code === profile.address.country)?.name || profile.address.country;
        return `${province}, ${countryName}`;
      }
      
      // If no country in address, just show province
      return province;
    }
    
    // If location doesn't have comma, show as-is
    return profile.location;
  }
  
  return null;
};

const ProfilePage = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { user, isAuthenticated, loadUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnUrl: '/profile' } });
      return;
    }
    loadUserProfile();
    loadPremiumStatus();
  }, [isAuthenticated, navigate]);

  const loadUserProfile = async () => {
    try {
      const data = await userAPI.getCurrentUser();
      setUserProfile(data.user);
    } catch (error) {
      console.error('Failed to load user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPremiumStatus = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load premium status:', error);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await premiumAPI.cancelSubscription();
      
      toast({
        title: "Subscription Cancelled",
        description: "Your premium subscription has been cancelled successfully.",
      });
      
      // Reload user data and premium status
      await loadUser();
      await loadPremiumStatus();
      
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        <BackButton />
        
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <Button 
            onClick={() => navigate('/edit-profile')}
            className="bg-mint-500 hover:bg-mint-600 text-white"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Picture & Basic Info */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                {/* Profile Image */}
                <div className="relative mb-4">
                  {userProfile.image ? (
                    <img
                      src={userProfile.image}
                      alt={`${userProfile.firstName} ${userProfile.lastName}`}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                      <User className="w-20 h-20 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Premium Badge */}
                  {premiumStatus?.isPremium && (
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full flex items-center text-sm font-semibold shadow-lg">
                      <Crown className="w-4 h-4 mr-1" />
                      Premium
                    </div>
                  )}
                </div>

                {/* Name & Age */}
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-2 flex items-center justify-center gap-2">
                  {userProfile.firstName} {userProfile.lastName}
                  {userProfile.govIdVerified && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      <CheckCircle className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                </h2>
                <p className="text-gray-600 text-center mb-4">
                  {userProfile.age} years | {userProfile.gender}
                </p>

                {/* Quick Stats */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center text-gray-700">
                    <Mail className="w-5 h-5 mr-3 text-navy-500" />
                    <span className="text-sm">{userProfile.email}</span>
                  </div>
                  {userProfile.phone && (
                    <div className="flex items-center text-gray-700">
                      <Phone className="w-5 h-5 mr-3 text-navy-500" />
                      <span className="text-sm">{userProfile.phone}</span>
                    </div>
                  )}
                  {getLocationDisplay(userProfile) && (
                    <div className="flex items-center text-gray-700">
                      <MapPin className="w-5 h-5 mr-3 text-navy-500" />
                      <span className="text-sm">{getLocationDisplay(userProfile)}</span>
                    </div>
                  )}
                </div>

                {/* Premium Status */}
                {premiumStatus && (
                  <div className="mt-4 pt-4 border-t">
                    {premiumStatus.isPremium ? (
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center text-green-800 font-semibold mb-2">
                            <CheckCircle className="w-5 h-5 mr-2" />
                            {premiumStatus.planName} Member
                          </div>
                          {premiumStatus.expiryDate && (
                            <p className="text-sm text-green-700 mb-2">
                              Valid until: {new Date(premiumStatus.expiryDate).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                          )}
                          {/* Show contact view limit */}
                          {premiumStatus.contactLimit !== -1 && (
                            <div className="mt-2 pt-2 border-t border-green-200">
                              <p className="text-sm text-green-700">
                                Contacts viewed: {premiumStatus.contactsViewed || 0} / {premiumStatus.contactLimit}
                              </p>
                              <div className="w-full bg-green-200 rounded-full h-2 mt-1">
                                <div 
                                  className="bg-green-600 h-2 rounded-full" 
                                  style={{width: `${Math.min(100, ((premiumStatus.contactsViewed || 0) / premiumStatus.contactLimit) * 100)}%`}}
                                ></div>
                              </div>
                            </div>
                          )}
                          {premiumStatus.contactLimit === -1 && (
                            <p className="text-sm text-green-700 mt-2">
                              ✨ Unlimited contact views
                            </p>
                          )}
                        </div>
                        
                        {/* Cancel Subscription Button */}
                        <Button 
                          onClick={() => setShowCancelConfirm(true)}
                          disabled={cancelling}
                          variant="outline"
                          className="w-full border-mint-500 text-navy-500 hover:bg-cream-100 touch-manipulation"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                        </Button>
                        
                        {/* Show Upgrade button if not Diamond */}
                        {premiumStatus.planName !== 'Diamond' && (
                          <Button 
                            onClick={() => navigate('/pricing')}
                            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white"
                          >
                            <Star className="w-4 h-4 mr-2" />
                            Upgrade to Diamond
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button 
                        onClick={() => navigate('/pricing')}
                        className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Upgrade to Premium
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Identity Verification Badge Card */}
            <Card className={`mt-4 border-2 ${userProfile?.govIdVerified ? 'border-green-200 bg-green-50' : userProfile?.govIdVerificationStatus === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-blue-200 bg-blue-50'}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${userProfile?.govIdVerified ? 'bg-green-100' : userProfile?.govIdVerificationStatus === 'pending' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                    {userProfile?.govIdVerified ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : userProfile?.govIdVerificationStatus === 'pending' ? (
                      <Clock className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <Shield className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    {userProfile?.govIdVerified ? (
                      <>
                        <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Verified Badge Earned
                        </h3>
                        <p className="text-sm text-green-700">
                          Your government ID has been verified. You have a verified badge on your profile.
                        </p>
                      </>
                    ) : userProfile?.govIdVerificationStatus === 'pending' ? (
                      <>
                        <h3 className="font-semibold text-yellow-900 mb-2">Verification Under Review</h3>
                        <p className="text-sm text-yellow-700">
                          Your ID document is being reviewed. You&apos;ll receive an email within 24-48 hours.
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="font-semibold text-blue-900 mb-2">Get Your Verified Badge</h3>
                        <p className="text-sm text-blue-700 mb-3">
                          Upload your government ID to get a verified badge on your profile. This builds trust with potential matches.
                        </p>
                        <Button 
                          onClick={() => navigate('/verification')}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Get Verified
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Detailed Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* About Section */}
            {userProfile.about && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-navy-500" />
                    About Me
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{userProfile.about}</p>
                </CardContent>
              </Card>
            )}

            {/* Personal Details */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-navy-500" />
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Age</p>
                      <p className="font-medium">{userProfile.age} years</p>
                    </div>
                  </div>
                  {userProfile.height && (
                    <div className="flex items-start">
                      <User className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Height</p>
                        <p className="font-medium">{userProfile.height}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.weight && (
                    <div className="flex items-start">
                      <User className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Weight</p>
                        <p className="font-medium">{userProfile.weight} kg</p>
                      </div>
                    </div>
                  )}
                  {userProfile.build && (
                    <div className="flex items-start">
                      <User className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Build</p>
                        <p className="font-medium">{userProfile.build}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.religion && (
                    <div className="flex items-start">
                      <Heart className="w-5 h-5 mr-3 text-coral-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Religion</p>
                        <p className="font-medium">{userProfile.religion}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.caste && (
                    <div className="flex items-start">
                      <Users className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Sect</p>
                        <p className="font-medium">{userProfile.caste}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.motherTongue && (
                    <div className="flex items-start">
                      <Mail className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Language</p>
                        <p className="font-medium">{userProfile.motherTongue}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.maritalStatus && (
                    <div className="flex items-start">
                      <Heart className="w-5 h-5 mr-3 text-coral-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Marital Status</p>
                        <p className="font-medium">{userProfile.maritalStatus}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Professional Details */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-navy-500" />
                  Professional Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userProfile.education && (
                    <div className="flex items-start">
                      <GraduationCap className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Regular Education</p>
                        <p className="font-medium">{userProfile.education}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.islamicEducation && (
                    <div className="flex items-start">
                      <GraduationCap className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Islamic Education</p>
                        <p className="font-medium">{userProfile.islamicEducation}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.jobCategory && (
                    <div className="flex items-start">
                      <Briefcase className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Job Category</p>
                        <p className="font-medium">{userProfile.jobCategory}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.occupation && (
                    <div className="flex items-start">
                      <Briefcase className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Job Title / Occupation</p>
                        <p className="font-medium">{userProfile.occupation}</p>
                      </div>
                    </div>
                  )}
                  {userProfile.income && (
                    <div className="flex items-start">
                      <Briefcase className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Income Range</p>
                        <p className="font-medium">{userProfile.income}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Family Details */}
            {userProfile.familyDetails && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-navy-500" />
                    Family Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {userProfile.familyDetails.familyStatus && (
                      <div className="flex items-start">
                        <Users className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Family Status</p>
                          <p className="font-medium">{userProfile.familyDetails.familyStatus}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.familyDetails.familyType && (
                      <div className="flex items-start">
                        <Users className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Family Type</p>
                          <p className="font-medium">{userProfile.familyDetails.familyType}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.familyDetails.fatherOccupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Father&apos;s Occupation</p>
                          <p className="font-medium">{userProfile.familyDetails.fatherOccupation}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.familyDetails.motherOccupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Mother&apos;s Occupation</p>
                          <p className="font-medium">{userProfile.familyDetails.motherOccupation}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.familyDetails.siblings && (
                      <div className="flex items-start">
                        <Users className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Siblings</p>
                          <p className="font-medium">{userProfile.familyDetails.siblings}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Religious Information */}
            {userProfile.religiousInfo && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Star className="w-5 h-5 mr-2 text-navy-500" />
                    Religious Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {userProfile.religiousInfo.madhhab && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Madhhab</p>
                          <p className="font-medium">{userProfile.religiousInfo.madhhab}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.religiousInfo.prayerHabit && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Prayer Habit</p>
                          <p className="font-medium">{userProfile.religiousInfo.prayerHabit}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.religiousInfo.beard && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Beard</p>
                          <p className="font-medium">{userProfile.religiousInfo.beard}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.religiousInfo.hijabNiqab && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Hijab/Niqab</p>
                          <p className="font-medium">{userProfile.religiousInfo.hijabNiqab}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.religiousInfo.quranRecitation && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Quran Recitation</p>
                          <p className="font-medium">{userProfile.religiousInfo.quranRecitation}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.religiousInfo.religiousCommitment && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Religious Commitment</p>
                          <p className="font-medium">{userProfile.religiousInfo.religiousCommitment}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.religiousInfo.islamicPractices && (
                      <div className="flex items-start">
                        <Star className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Islamic Practices</p>
                          <p className="font-medium">{userProfile.religiousInfo.islamicPractices}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Partner Preferences */}
            {userProfile.partnerPreferences && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2 text-navy-500" />
                    Partner Preferences
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {userProfile.partnerPreferences.ageRange && (
                      <div className="flex items-start">
                        <Calendar className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Age Range</p>
                          <p className="font-medium">{userProfile.partnerPreferences.ageRange}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.partnerPreferences.heightRange && (
                      <div className="flex items-start">
                        <User className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Height Range</p>
                          <p className="font-medium">{userProfile.partnerPreferences.heightRange}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.partnerPreferences.maritalStatus && (
                      <div className="flex items-start">
                        <Heart className="w-5 h-5 mr-3 text-coral-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Marital Status</p>
                          <p className="font-medium">{userProfile.partnerPreferences.maritalStatus}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.partnerPreferences.religion && (
                      <div className="flex items-start">
                        <Heart className="w-5 h-5 mr-3 text-coral-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Religion</p>
                          <p className="font-medium">{userProfile.partnerPreferences.religion}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.partnerPreferences.education && (
                      <div className="flex items-start">
                        <GraduationCap className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Education</p>
                          <p className="font-medium">{userProfile.partnerPreferences.education}</p>
                        </div>
                      </div>
                    )}
                    {userProfile.partnerPreferences.occupation && (
                      <div className="flex items-start">
                        <Briefcase className="w-5 h-5 mr-3 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">Occupation</p>
                          <p className="font-medium">{userProfile.partnerPreferences.occupation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelSubscription}
        title="Cancel Subscription?"
        message="Are you sure you want to cancel your premium subscription? You will lose access to all premium features immediately."
        confirmText="Yes, Cancel"
        cancelText="Keep Premium"
        variant="danger"
      />
    </div>
  );
};

export default ProfilePage;
